import type {
  CoupledDimensionGroupResult,
  DimensionSource,
  WidgetDimensionsResponse,
} from '@highimpact-frontend/api-types';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  controlPanelSelectorBoxSx,
  FilterDefinition,
  FilterOption,
  filterOptionsByFilterChipLabel,
  filterOptionsByInputLabel,
  getCoupledGroupLabel,
  getFilterOptionLabel,
  getFilterOptionsAt,
  getIndependentDimensionLabel,
  matchesSelectedFilters,
  mergeOptionsByKey,
  renderEllipsizedOption,
  renderFilterTags,
  renderLabelTags,
  sameFilterOption,
  sanitizeFiltersByHierarchy,
  toFilterRecord,
  toFrenchGroupLabel,
  uniqueValues,
} from './selectorUi';
import {
  getCoupledDimensionRowLabel,
  getDimensionSourceKey,
} from './sourceMappers';

// -- type definitions --

type FilterableRecord = {
  dimensions: Record<string, string>;
};

type DimensionRecord = FilterableRecord & {
  source: DimensionSource;
};

type CoupledRowRecord = FilterableRecord & {
  row: CoupledDimensionGroupResult;
};

export interface V2DimensionsSelectorProps {
  data: WidgetDimensionsResponse;
  onIndependentDimensionSelect?: (
    dimensionKey: string,
    selected: DimensionSource[]
  ) => void;
  onCoupledGroupSelect?: (
    groupKey: string,
    selectedRows: CoupledDimensionGroupResult[]
  ) => void;
  initialIndependentSelection?: Record<string, DimensionSource[]>;
  initialCoupledSelection?: Record<string, CoupledDimensionGroupResult[]>;
  matchControlPanelStyle?: boolean;
  /** When true, coupled groups allow only one aligned row. Defaults to true. */
  coupledGroupsSingleSelect?: boolean;
  /** Per-dimension multi-select toggles. Defaults to true for every slot. */
  independentDimensionMultiple?: Record<string, boolean>;
  /** Optional display labels keyed by dimension slot name. */
  independentDimensionLabels?: Record<string, string>;
  /** Optional display labels keyed by coupled group name. */
  coupledGroupLabels?: Record<string, string>;
}

// -- helper functions --

const DIMENSION_FILTER_FIELDS: ReadonlyArray<{
  key: string;
  label: string;
  readValue: (source: DimensionSource) => string | null | undefined;
}> = [
  { key: 'project', label: 'project', readValue: (s) => s.project_name },
  { key: 'site', label: 'site', readValue: (s) => s.site_name },
  {
    key: 'device_type',
    label: 'device_type',
    readValue: (s) => s.device_type_name,
  },
  { key: 'thematic', label: 'thematic', readValue: (s) => s.thematic_name },
  { key: 'timezone', label: 'timezone', readValue: (s) => s.timezone },
  {
    key: 'physical_quantity',
    label: 'physical_quantity',
    readValue: (s) => s.physical_quantity,
  },
  { key: 'unit', label: 'unit', readValue: (s) => s.measurement_unit },
];

function buildDimensionsFromSource(
  source: DimensionSource
): Record<string, string> {
  return Object.fromEntries(
    DIMENSION_FILTER_FIELDS.flatMap((field) => {
      const value = field.readValue(source);
      return value ? [[field.key, value]] : [];
    })
  );
}

function buildDimensionRecords(
  sources: readonly DimensionSource[]
): DimensionRecord[] {
  return sources.map((source) => ({
    source,
    dimensions: buildDimensionsFromSource(source),
  }));
}

function buildCoupledRowRecords(
  rows: readonly CoupledDimensionGroupResult[]
): CoupledRowRecord[] {
  return rows.map((row) => {
    const representativeSource = Object.values(row)[0];

    return {
      row,
      dimensions: representativeSource
        ? buildDimensionsFromSource(representativeSource)
        : {},
    };
  });
}

function buildFilterDefinitions(
  records: readonly FilterableRecord[]
): FilterDefinition[] {
  return DIMENSION_FILTER_FIELDS.flatMap((field) => {
    const distinctValues = uniqueValues(
      records
        .map((record) => record.dimensions[field.key])
        .filter((value): value is string => Boolean(value))
    );

    if (distinctValues.length <= 1) {
      return [];
    }

    return [{ key: field.key, label: field.label }];
  });
}

function sameDimensionSource(
  left: DimensionSource,
  right: DimensionSource
): boolean {
  return getDimensionSourceKey(left) === getDimensionSourceKey(right);
}

function getCoupledRowKey(row: CoupledDimensionGroupResult): string {
  return Object.values(row)
    .map((source) => getDimensionSourceKey(source))
    .sort()
    .join('|');
}

function sameCoupledRow(
  left: CoupledDimensionGroupResult,
  right: CoupledDimensionGroupResult
): boolean {
  return getCoupledRowKey(left) === getCoupledRowKey(right);
}

function filterSourcesByActiveFilters(
  records: readonly DimensionRecord[],
  filters: Record<string, string[]>,
  definitions: readonly FilterDefinition[]
): DimensionSource[] {
  return records
    .filter((record) =>
      matchesSelectedFilters(record.dimensions, filters, definitions)
    )
    .map((record) => record.source);
}

function filterCoupledRowsByActiveFilters(
  records: readonly CoupledRowRecord[],
  filters: Record<string, string[]>,
  definitions: readonly FilterDefinition[]
): CoupledDimensionGroupResult[] {
  return records
    .filter((record) =>
      matchesSelectedFilters(record.dimensions, filters, definitions)
    )
    .map((record) => record.row);
}

interface DimensionFilterBarProps {
  records: readonly FilterableRecord[];
  filterDefinitions: readonly FilterDefinition[];
  filters: Record<string, string[]>;
  onFiltersChange: (filters: Record<string, string[]>) => void;
  testId?: string;
}

const DimensionFilterBar: React.FC<DimensionFilterBarProps> = ({
  records,
  filterDefinitions,
  filters,
  onFiltersChange,
  testId,
}) => {
  const allFilterOptions = useMemo(
    () =>
      filterDefinitions.flatMap((definition, index) =>
        getFilterOptionsAt(records, filterDefinitions, filters, index).map(
          (value) => ({
            key: definition.key,
            label: definition.label,
            value,
          })
        )
      ),
    [filterDefinitions, filters, records]
  );

  const selectedFilterOptions = useMemo(
    () =>
      filterDefinitions.flatMap((definition) =>
        (filters[definition.key] ?? []).map((value) => ({
          key: definition.key,
          label: definition.label,
          value,
        }))
      ),
    [filterDefinitions, filters]
  );

  if (filterDefinitions.length === 0) {
    return null;
  }

  const handleFiltersChange = (selectedOptions: FilterOption[]) => {
    const candidateFilters = toFilterRecord(selectedOptions);
    const nextFilters = sanitizeFiltersByHierarchy(
      (index, activeFilters) =>
        getFilterOptionsAt(records, filterDefinitions, activeFilters, index),
      filterDefinitions,
      candidateFilters
    );
    onFiltersChange(nextFilters);
  };

  return (
    <Autocomplete
      multiple
      options={allFilterOptions}
      value={selectedFilterOptions}
      onChange={(_, values) => handleFiltersChange(values)}
      isOptionEqualToValue={sameFilterOption}
      filterOptions={filterOptionsByFilterChipLabel}
      openOnFocus
      groupBy={(option) => toFrenchGroupLabel(option.label, option.key)}
      getOptionLabel={getFilterOptionLabel}
      renderTags={renderFilterTags}
      renderOption={(props, option) =>
        renderEllipsizedOption(props, getFilterOptionLabel(option))
      }
      size="small"
      renderInput={(params) => (
        <TextField {...params} label="Filtres" size="small" />
      )}
      data-testid={testId}
    />
  );
};

// -- sub components --

interface IndependentDimensionPickerProps {
  dimensionKey: string;
  label: string;
  sources: readonly DimensionSource[];
  selectedSources: DimensionSource[];
  onSelect: (selected: DimensionSource[]) => void;
  multiple: boolean;
}

const IndependentDimensionPicker: React.FC<IndependentDimensionPickerProps> = ({
  dimensionKey,
  label,
  sources,
  selectedSources,
  onSelect,
  multiple,
}) => {
  const records = useMemo(() => buildDimensionRecords(sources), [sources]);
  const filterDefinitions = useMemo(
    () => buildFilterDefinitions(records),
    [records]
  );
  const [filters, setFilters] = useState<Record<string, string[]>>({});

  const filteredSources = useMemo(
    () => filterSourcesByActiveFilters(records, filters, filterDefinitions),
    [records, filters, filterDefinitions]
  );

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <DimensionFilterBar
        records={records}
        filterDefinitions={filterDefinitions}
        filters={filters}
        onFiltersChange={setFilters}
      />

      <Autocomplete
        multiple={multiple}
        options={mergeOptionsByKey(
          filteredSources,
          selectedSources,
          sameDimensionSource
        )}
        value={multiple ? selectedSources : selectedSources[0] ?? null}
        getOptionLabel={(source) => source.label}
        isOptionEqualToValue={sameDimensionSource}
        filterOptions={filterOptionsByInputLabel}
        openOnFocus
        onChange={(_, value) => {
          if (multiple) {
            onSelect((value as DimensionSource[]) ?? []);
            return;
          }

          onSelect(value ? [value as DimensionSource] : []);
        }}
        renderTags={
          multiple
            ? (values, getTagProps) =>
                renderLabelTags(values, getTagProps, getDimensionSourceKey)
            : undefined
        }
        renderOption={(props, source) =>
          renderEllipsizedOption(props, source.label)
        }
        size="small"
        renderInput={(params) => (
          <TextField {...params} label={label} size="small" />
        )}
        data-testid={`independent-dimension-${dimensionKey}`}
      />
    </Box>
  );
};

interface CoupledGroupPickerProps {
  groupKey: string;
  label: string;
  rows: readonly CoupledDimensionGroupResult[];
  selectedRows: CoupledDimensionGroupResult[];
  onSelect: (selectedRows: CoupledDimensionGroupResult[]) => void;
  singleSelect: boolean;
}

const CoupledGroupPicker: React.FC<CoupledGroupPickerProps> = ({
  groupKey,
  label,
  rows,
  selectedRows,
  onSelect,
  singleSelect,
}) => {
  const rowRecords = useMemo(() => buildCoupledRowRecords(rows), [rows]);
  const filterDefinitions = useMemo(
    () => buildFilterDefinitions(rowRecords),
    [rowRecords]
  );
  const [filters, setFilters] = useState<Record<string, string[]>>({});

  const filteredRows = useMemo(
    () =>
      filterCoupledRowsByActiveFilters(rowRecords, filters, filterDefinitions),
    [rowRecords, filters, filterDefinitions]
  );

  const options = useMemo(
    () =>
      mergeOptionsByKey(filteredRows, selectedRows, sameCoupledRow).map(
        (row) => ({
          row,
          label: getCoupledDimensionRowLabel(row),
        })
      ),
    [filteredRows, selectedRows]
  );

  const selectedOptions = useMemo(
    () =>
      selectedRows.map((row) => ({
        row,
        label: getCoupledDimensionRowLabel(row),
      })),
    [selectedRows]
  );

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <DimensionFilterBar
        records={rowRecords}
        filterDefinitions={filterDefinitions}
        filters={filters}
        onFiltersChange={setFilters}
        testId={`coupled-group-filters-${groupKey}`}
      />

      <Autocomplete
        multiple={!singleSelect}
        options={options}
        value={singleSelect ? selectedOptions[0] ?? null : selectedOptions}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(left, right) =>
          sameCoupledRow(left.row, right.row)
        }
        filterOptions={filterOptionsByInputLabel}
        openOnFocus
        onChange={(_, value) => {
          if (singleSelect) {
            const selectedOption = value as {
              row: CoupledDimensionGroupResult;
              label: string;
            } | null;
            onSelect(selectedOption ? [selectedOption.row] : []);
            return;
          }

          const selectedOptionsList =
            (value as Array<{
              row: CoupledDimensionGroupResult;
              label: string;
            }>) ?? [];
          onSelect(selectedOptionsList.map((option) => option.row));
        }}
        renderOption={(props, option) =>
          renderEllipsizedOption(props, option.label)
        }
        size="small"
        renderInput={(params) => (
          <TextField {...params} label={label} size="small" />
        )}
        data-testid={`coupled-group-${groupKey}`}
      />
    </Box>
  );
};

// -- main component --

/**
 * Selector for v2 flat dimension responses (independent axes and coupled groups).
 */
export const V2DimensionsSelector: React.FC<V2DimensionsSelectorProps> = ({
  data,
  onIndependentDimensionSelect,
  onCoupledGroupSelect,
  initialIndependentSelection,
  initialCoupledSelection,
  matchControlPanelStyle = false,
  coupledGroupsSingleSelect = true,
  independentDimensionMultiple,
  independentDimensionLabels,
  coupledGroupLabels,
}) => {
  const [independentSelections, setIndependentSelections] = useState<
    Record<string, DimensionSource[]>
  >({});
  const [coupledSelections, setCoupledSelections] = useState<
    Record<string, CoupledDimensionGroupResult[]>
  >({});
  const lastIndependentSelectionKeyRef = useRef<string>('');

  const independentDimensions = data.independent_dimensions ?? {};
  const coupledGroups = data.coupled_groups ?? {};

  const independentSelectionKey = useMemo(
    () => JSON.stringify(initialIndependentSelection ?? {}),
    [initialIndependentSelection]
  );

  useEffect(() => {
    if (
      !initialIndependentSelection ||
      independentSelectionKey === lastIndependentSelectionKeyRef.current
    ) {
      return;
    }

    lastIndependentSelectionKeyRef.current = independentSelectionKey;
    setIndependentSelections(initialIndependentSelection);
  }, [initialIndependentSelection, independentSelectionKey]);

  useEffect(() => {
    if (initialCoupledSelection) {
      setCoupledSelections(initialCoupledSelection);
    }
  }, [initialCoupledSelection]);

  const content = (
    <>
      {Object.entries(independentDimensions).map(([dimensionKey, sources]) => (
        <IndependentDimensionPicker
          key={dimensionKey}
          dimensionKey={dimensionKey}
          label={
            independentDimensionLabels?.[dimensionKey] ??
            getIndependentDimensionLabel(dimensionKey)
          }
          sources={sources}
          selectedSources={independentSelections[dimensionKey] ?? []}
          multiple={independentDimensionMultiple?.[dimensionKey] ?? true}
          onSelect={(selected) => {
            const nextSelections = {
              ...independentSelections,
              [dimensionKey]: selected,
            };
            setIndependentSelections(nextSelections);
            onIndependentDimensionSelect?.(dimensionKey, selected);
          }}
        />
      ))}

      {Object.entries(coupledGroups).map(([groupKey, rows]) => (
        <CoupledGroupPicker
          key={groupKey}
          groupKey={groupKey}
          label={
            coupledGroupLabels?.[groupKey] ?? getCoupledGroupLabel(groupKey)
          }
          rows={rows}
          selectedRows={coupledSelections[groupKey] ?? []}
          singleSelect={coupledGroupsSingleSelect}
          onSelect={(selectedRows) => {
            const nextSelections = {
              ...coupledSelections,
              [groupKey]: selectedRows,
            };
            setCoupledSelections(nextSelections);
            onCoupledGroupSelect?.(groupKey, selectedRows);
          }}
        />
      ))}
    </>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      p={matchControlPanelStyle ? 2 : 0}
      width={matchControlPanelStyle ? 300 : undefined}
    >
      {matchControlPanelStyle ? (
        <>
          <Typography
            variant="subtitle2"
            sx={{ fontSize: '0.9rem', fontWeight: 700 }}
          >
            Sources de données
          </Typography>
          <Box sx={controlPanelSelectorBoxSx}>{content}</Box>
        </>
      ) : (
        content
      )}
    </Box>
  );
};
